import { ChangeEvent, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { formatBusinessDate } from "./saleCenterClosing.utils";

const REASON_LABEL = "เหตุผลที่ส่งกลับ";
const REASON_REQUIRED_ERROR = "กรุณาระบุเหตุผลที่ส่งกลับ";

interface SendBackClosingDialogProps {
  open: boolean;
  branchCode: string;
  businessDate: string; // YYYY-MM-DD
  saving: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

/**
 * ส่งกลับให้สาขาแก้ไข — the supervisor returning a branch-day to the branch.
 *
 * The reason is mandatory because sending back is destructive in a way the branch cannot
 * reconstruct: every ตรวจแล้ว tick in the branch-day is cleared, so people who had already
 * signed have to sign again, and the stored reason is the only record of why. Whitespace does
 * not count as a reason — the confirm stays disabled until something has actually been typed.
 */
export default function SendBackClosingDialog({
  open,
  branchCode,
  businessDate,
  saving,
  onClose,
  onConfirm,
}: SendBackClosingDialogProps) {
  const [reason, setReason] = useState<string>("");

  // A reason belongs to one send-back only; carrying the previous one over would let it be
  // submitted against a different branch-day by accident.
  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const trimmedReason = reason.trim();
  const isReasonBlank = trimmedReason === "";

  const handleChangeReason = (event: ChangeEvent<HTMLInputElement>) => {
    setReason(event.target.value);
  };

  const handleConfirm = () => {
    if (isReasonBlank) return;
    onConfirm(trimmedReason);
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>ส่งกลับให้สาขาแก้ไข</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <Typography variant="body2">
            สาขา {branchCode} วันที่ {formatBusinessDate(businessDate)}
          </Typography>
          <Alert severity="warning">
            สถานะ ตรวจแล้ว ทั้งฝั่ง BO และ HO ของสาขานี้ในวันดังกล่าวจะถูกล้างทั้งหมด
            และสาขาจะกลับมาแก้ไขข้อมูลได้อีกครั้ง ผู้ตรวจสอบต้องตรวจใหม่หลังสาขาแก้ไขเสร็จ
          </Alert>
          <TextField
            label={REASON_LABEL}
            value={reason}
            onChange={handleChangeReason}
            size="small"
            fullWidth
            multiline
            minRows={3}
            disabled={saving}
            error={reason !== "" && isReasonBlank}
            helperText={
              reason !== "" && isReasonBlank
                ? REASON_REQUIRED_ERROR
                : undefined
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={handleClose} disabled={saving}>
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={saving || isReasonBlank}
        >
          ยืนยันส่งกลับ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
